/**
 * saleTicketGlobalSections.ts — Bloques GLOBALES del ticket de venta térmico.
 *
 * Los 3 formatos (completo / compacto / ultracompacto) comparten estas zonas y
 * deben verse IDÉNTICAS en la vista térmica:
 *  1. Cabecera de negocio + título «COMPROBANTE DE VENTA #N» + meta.
 *  2. Totales (subtotal → TOTAL A PAGAR).
 *  3. Forma de pago + nota.
 *  4. Programa de puntos (cuando el comprobante lo requiere).
 *
 * QUÉ NO HACE: no pinta filas de ítems (eso es por formato), no decide formato,
 * no persiste. Devuelve líneas listas para el flujo ESC/POS.
 *
 * REGLA DE PARIDAD: los builders solo diferencian el DETALLE (filas); el resto
 * debe salir de estas funciones para que los tres formatos sean iguales.
 */

import {
  appendTwoColWrapped,
  getTicketMaxWidth,
  padCol,
  sanitizeForThermal,
  wrapThermalText,
} from "./escposLayout";
import {
  formatTicketSaleDate,
  formatTicketSaleTime,
} from "@/lib/utils/formatters";
import type { CartItem } from "@/types";
import { buildTicketTotalesModel } from "./ticketTotalesModel";
import {
  buildSaleTicketUsageFeeRows,
  buildClienteSurchargeTicketNote,
  resolveSaleTicketPaymentLines,
  saleTicketUsageFeeLabel,
} from "./paymentMethodUsageFee";
import { formatIncentivePct } from "./paymentMethodIncentive";
import type { SaleTicketEscPosInput, BusinessInfo } from "./saleTicketEscPos";
import { loadBusinessForReceipt } from "./saleTicketEscPos";

/** Carácter del marco grueso (`====`). */
const GLOBAL_THICK_RULE = "=";
/** Carácter de las separaciones finas (`----`). */
export const GLOBAL_THIN_RULE = "-";

/** Título de la zona de detalle (común a todos los formatos). */
const GLOBAL_DETALLE_TITLE = "DETALLE DE VENTA";

function money(n: number): string {
  return `$${Math.round(n).toLocaleString("es-CO")}`;
}

/** Línea de texto simple rellenada a ancho completo (evita re-centrado del decode). */
function pushInline(lines: string[], text: string, width: number): void {
  lines.push(
    sanitizeForThermal(text.replace(/\s+/g, " ").trim().padEnd(width, " ")),
  );
}

/** Línea alineada a la DERECHA (termina en el borde del papel). */
function pushRight(lines: string[], text: string, width: number): void {
  const clean = text.replace(/\s+/g, " ").trim();
  if (!clean) return;
  if (clean.length <= width) {
    lines.push(
      sanitizeForThermal(`${" ".repeat(width - clean.length)}${clean}`),
    );
    return;
  }
  for (const ln of wrapThermalText(clean, width)) {
    const l = ln.replace(/\s+/g, " ").trim();
    lines.push(sanitizeForThermal(`${" ".repeat(width - l.length)}${l}`));
  }
}

function pushCenter(lines: string[], text: string, width: number): void {
  lines.push(sanitizeForThermal(padCol(text, width, "center")));
}

/**
 * Centra texto largo envolviéndolo (dirección/NIT): un dato que no cabe en el
 * papel (ej. NIT en 58mm) JAMÁS se corta — sale en la siguiente línea.
 */
function pushCenterWrapped(lines: string[], text: string, width: number): void {
  const clean = (text || "").replace(/\s+/g, " ").trim();
  if (!clean) return;
  for (const part of wrapThermalText(clean, width)) {
    pushCenter(lines, part, width);
  }
}

/** Regla fina de separación interna (guiones) — distinta de las de sección. */
function pushRule(lines: string[], width: number, char: string): void {
  lines.push(sanitizeForThermal(char.repeat(Math.max(1, width))));
}

/** Rellena un monto a la columna fija de totales (derecha). */
function padMoney(total: string, moneyW: number): string {
  return total.length >= moneyW
    ? total
    : " ".repeat(moneyW - total.length) + total;
}

/** Línea label/valor con el valor ALINEADO a la columna de totales. */
function pushTwoCol(
  lines: string[],
  left: string,
  right: string,
  width: number,
): void {
  // El monto NUNCA se trunca: si la etiqueta es larga (ej. «Descuento General
  // (10%)» en 58mm) se envuelve y el valor queda completo a la derecha.
  appendTwoColWrapped(lines, left, right, width);
}

/** Detalle envuelto e indentado (notas, aclaraciones). */
function pushDetailLine(
  lines: string[],
  detail: string,
  width: number,
  indent = 2,
): void {
  const text = detail.replace(/\s+/g, " ").trim();
  if (!text) return;
  const bodyW = Math.max(8, width - indent);
  const wrapped = text.length <= bodyW ? [text] : wrapThermalText(text, bodyW);
  for (const ln of wrapped) {
    lines.push(
      sanitizeForThermal(`${" ".repeat(indent)}${ln}`.padEnd(width, " ")),
    );
  }
}

/** Une dos pares label/valor en una línea si caben; si no, dos líneas. */
function pushMetaPair(
  lines: string[],
  aLabel: string,
  aValue: string,
  bLabel: string,
  bValue: string,
  width: number,
): void {
  const a = `${aLabel} ${aValue}`.trim();
  const b = `${bLabel} ${bValue}`.trim();
  const combined = `${a}   ${b}`;
  if (combined.length <= width) {
    lines.push(sanitizeForThermal(combined.padEnd(width, " ")));
    return;
  }
  lines.push(sanitizeForThermal(a.padEnd(width, " ")));
  lines.push(sanitizeForThermal(b.padEnd(width, " ")));
}

/** Etiqueta visible del método de pago (fallback al snapshot de la venta). */
function formatMetodo(method: string, label?: string): string {
  if (label && label.trim()) return label.trim();
  const m: Record<string, string> = {
    efectivo: "Efectivo",
    tarjeta: "Tarjeta",
    nequi: "Nequi",
    daviplata: "Daviplata",
    transferencia: "Transferencia",
    mixto: "Mixto",
    credito: "Venta a crédito",
  };
  return m[method] ?? method;
}

/**
 * Cabecera global: marco `====`, negocio centrado, título con # y filas de meta.
 * No cierra con regla: el builder cierra (y abre DETALLE) con una fina.
 */
export function buildGlobalHeaderLines(
  input: SaleTicketEscPosInput,
  biz: BusinessInfo,
): string[] {
  const lines: string[] = [];
  const width = getTicketMaxWidth(input.paperWidthMm);
  const d = input.ticketDate ?? new Date();

  pushRule(lines, width, GLOBAL_THICK_RULE);
  pushCenter(lines, biz.nombre.toUpperCase(), width);
  if (biz.slogan?.trim()) pushCenter(lines, biz.slogan.trim(), width);

  const direccion = biz.direccion?.trim();
  const nit = biz.nit?.trim() ? `NIT: ${biz.nit.trim()}` : "";
  if (direccion && nit) {
    // Datos legales/identidad: si no caben juntos, cada uno en su línea, sin
    // recortar nunca el NIT.
    pushCenterWrapped(lines, direccion, width);
    pushCenterWrapped(lines, nit, width);
  } else if (direccion || nit) {
    pushCenterWrapped(lines, direccion || nit, width);
  }
  pushRule(lines, width, GLOBAL_THICK_RULE);

  const numero =
    input.numeroVenta != null && input.numeroVenta !== ""
      ? `#${input.numeroVenta}`
      : "";
  const label = "COMPROBANTE DE VENTA";
  if (numero) {
    pushInline(lines, `${label}   ${numero}`, width);
  } else {
    pushCenter(lines, label, width);
  }
  pushRule(lines, width, GLOBAL_THIN_RULE);

  const fecha = formatTicketSaleDate(d);
  const hora = formatTicketSaleTime(d);
  pushMetaPair(lines, "Fecha:", fecha, "Hora:", hora, width);
  if (input.cajaNombre?.trim()) {
    pushInline(lines, `Caja: ${input.cajaNombre.trim()}`, width);
  }
  const vendedor = input.registradoPor?.trim();
  const sesion = input.sesionAbiertaPor?.trim();
  if (vendedor && sesion) {
    pushMetaPair(lines, "Vendedor:", vendedor, "Sesion de:", sesion, width);
  } else if (vendedor) {
    pushInline(lines, `Vendedor: ${vendedor}`, width);
  } else if (sesion) {
    pushInline(lines, `Sesion de: ${sesion}`, width);
  }

  return lines;
}

/**
 * Título del detalle común: regla fina + «DETALLE DE VENTA» + regla fina.
 * La columna/cabecera de ítems es por formato y la emite cada builder.
 */
export function buildGlobalDetailHeaderLines(
  input: SaleTicketEscPosInput,
): string[] {
  const lines: string[] = [];
  const width = getTicketMaxWidth(input.paperWidthMm);
  pushRule(lines, width, GLOBAL_THIN_RULE);
  pushCenter(lines, GLOBAL_DETALLE_TITLE, width);
  pushRule(lines, width, GLOBAL_THIN_RULE);
  return lines;
}

/**
 * Totales globales: desde Subtotal hasta TOTAL A PAGAR (con reglas finas).
 * El DETALLE ya cerró con su regla antes de llamar a esta función.
 */

export function buildGlobalTotalsLines(input: SaleTicketEscPosInput): string[] {
  const lines: string[] = [];
  const width = getTicketMaxWidth(input.paperWidthMm);
  const ticketPayLines = resolveSaleTicketPaymentLines({
    paymentMethod: input.paymentMethod,
    total: input.total,
    paymentLines: input.paymentLines,
  });
  const usageFeeRows = buildSaleTicketUsageFeeRows(ticketPayLines, (id, snap) =>
    formatMetodo(id, snap),
  );
  const recargoRows = usageFeeRows.filter((r) => r.paidBy === "cliente");

  // Modelo único de Totales: mismas filas que la Vista Web (Subtotal con IVA
  // + filas de IVA "(incluido)" informativas). Cada descuento aparece una vez.
  const model = buildTicketTotalesModel(
    (input.cart ?? []) as unknown as CartItem[],
    input.discountBreakdown,
    input.globalDiscount,
    loadBusinessForReceipt().impuestoDefaultPct ?? 0,
  );
  const rows = model.rows;

  // Si no hay conceptos intermedios, una única fila igual al total sobra.
  const hayIntermedios =
    rows.length > 1 ||
    (input.servicioAmount ?? 0) > 0 ||
    recargoRows.length > 0 ||
    ((input.incentiveAmount ?? 0) > 0 && (input.incentivePct ?? 0) > 0);

  rows.forEach((row, index) => {
    if (index === 0 && !hayIntermedios && row.amount === input.total) return;
    pushTwoCol(
      lines,
      row.label,
      row.tone === "warn" ? `-${money(row.amount)}` : money(row.amount),
      width,
    );
  });

  if ((input.servicioAmount ?? 0) > 0) {
    pushTwoCol(
      lines,
      `Servicio (${input.servicioPct ?? 0}%)`,
      money(input.servicioAmount ?? 0),
      width,
    );
  }

  for (const row of recargoRows) {
    pushTwoCol(
      lines,
      saleTicketUsageFeeLabel(row, "recargo"),
      `+${money(row.feeAmount)}`,
      width,
    );
  }
  if (recargoRows.length === 1) {
    pushDetailLine(
      lines,
      buildClienteSurchargeTicketNote(recargoRows[0]),
      width,
      0,
    );
  } else if (recargoRows.length > 1) {
    pushDetailLine(
      lines,
      "Comisiones sobre el total pagado con cada metodo: nunca sobre el precio.",
      width,
      0,
    );
  }
  if (
    input.incentiveAmount != null &&
    input.incentiveAmount > 0 &&
    input.incentivePct != null
  ) {
    pushTwoCol(
      lines,
      `Incentivo por pagar con ${input.incentiveMethodLabel ?? ""} (${formatIncentivePct(input.incentivePct)})`,
      `-${money(input.incentiveAmount)}`,
      width,
    );
  }
  pushRule(lines, width, GLOBAL_THIN_RULE);
  pushTwoCol(lines, "TOTAL A PAGAR", money(input.total), width);
  pushRule(lines, width, GLOBAL_THIN_RULE);

  return lines;
}

/**
 * Forma de pago global: bloque ALINEADO A LA DERECHA (termina en el borde del
 * papel). En Efectivo se imprime siempre «Método / Recibido / Cambio»
 * (Recibido y Cambio visibles aunque el cambio sea $0); en mixto una fila por
 * método + TOTAL PAGADO.
 */
export function buildGlobalPaymentLines(
  input: SaleTicketEscPosInput,
): string[] {
  const lines: string[] = [];
  const width = getTicketMaxWidth(input.paperWidthMm);
  const ticketPayLines = resolveSaleTicketPaymentLines({
    paymentMethod: input.paymentMethod,
    total: input.total,
    paymentLines: input.paymentLines,
  });

  if (input.formaPagoSimple) {
    pushRight(
      lines,
      formatMetodo(
        ticketPayLines[0]?.method ?? input.paymentMethod,
        ticketPayLines[0]?.methodLabel ?? input.paymentMethodLabel,
      ),
      width,
    );
    return lines;
  }

  if (ticketPayLines.length === 1) {
    const pl = ticketPayLines[0];
    const methodLabel = formatMetodo(pl.method, pl.methodLabel);
    if (pl.method === "efectivo") {
      const received = pl.received ?? pl.amount;
      const cambio = Math.max(0, Math.round(received - pl.amount));
      pushRight(lines, `Método: ${methodLabel}`, width);
      pushRight(lines, `Recibido: ${money(received)}`, width);
      pushRight(lines, `Cambio: ${money(cambio)}`, width);
    } else {
      pushRight(lines, `Método: ${methodLabel}`, width);
      pushRight(lines, `Valor: ${money(pl.amount)}`, width);
    }
    return lines;
  }

  for (const pl of ticketPayLines) {
    const methodLabel = formatMetodo(pl.method, pl.methodLabel);
    pushRight(lines, `Método: ${methodLabel}`, width);
    pushRight(lines, `Valor: ${money(pl.amount)}`, width);
    if (pl.method === "efectivo") {
      const received = pl.received ?? pl.amount;
      const cambio = Math.max(0, Math.round(received - pl.amount));
      pushRight(lines, `Recibido: ${money(received)}`, width);
      pushRight(lines, `Cambio: ${money(cambio)}`, width);
    }
  }
  const totalPagado = ticketPayLines.reduce((acc, r) => acc + r.amount, 0);
  pushRight(
    lines,
    input.paymentMethod === "credito"
      ? `TOTAL CREDITO (PEND.): ${money(input.total)}`
      : `TOTAL PAGADO: ${money(totalPagado)}`,
    width,
  );

  return lines;
}

/** Nota libre (se imprime bajo la forma de pago; solo si existe). */
export function buildGlobalNotaLines(input: SaleTicketEscPosInput): string[] {
  const lines: string[] = [];
  if (!input.note?.trim()) return lines;
  const width = getTicketMaxWidth(input.paperWidthMm);
  pushRight(lines, `Nota: ${input.note.trim()}`, width);
  return lines;
}

/** Fila de puntos: label a la izquierda y valor alineado al borde derecho. */
function pushPointsRow(
  lines: string[],
  left: string,
  right: string,
  width: number,
): void {
  const moneyW = 12;
  const R = right.replace(/\s+/g, " ").trim();
  const labelW = Math.max(8, width - moneyW - 1);
  const L = left.replace(/\s+/g, " ").trim();
  const wrap = L.length <= labelW ? [L] : wrapThermalText(L, labelW);
  const last = wrap[wrap.length - 1] ?? "";
  for (let i = 0; i < wrap.length - 1; i++) {
    lines.push(sanitizeForThermal(wrap[i].padEnd(labelW + 1, " ")));
  }
  const label =
    last.length > labelW ? last.slice(0, labelW) : last.padEnd(labelW);
  const val = padMoney(R, moneyW);
  lines.push(sanitizeForThermal(`${label} ${val}`));
}

/** Programa de puntos (solo en comprobantes completos que lo requieran). */
export function buildGlobalPuntosLines(input: SaleTicketEscPosInput): string[] {
  const lines: string[] = [];
  const width = getTicketMaxWidth(input.paperWidthMm);
  const hasPuntos =
    input.clientePuntosNombre &&
    (input.puntosGanados != null ||
      (input.premiosCanjeados && input.premiosCanjeados.length > 0));
  if (!hasPuntos) return lines;

  pushInline(lines, "PROGRAMA DE PUNTOS", width);
  const cliente = input.clientePuntosNombre?.trim();
  const codigo = input.clientePuntosCodigo?.trim();
  pushDetailLine(
    lines,
    codigo ? `Cliente: ${cliente} (${codigo})` : `Cliente: ${cliente}`,
    width,
    0,
  );
  if (input.puntosAnteriores != null) {
    pushPointsRow(
      lines,
      "Puntos anteriores:",
      `${input.puntosAnteriores} pts`,
      width,
    );
  }
  if (input.premiosCanjeados?.length) {
    pushDetailLine(lines, "Premios canjeados:", width, 0);
    for (const p of input.premiosCanjeados) {
      pushPointsRow(lines, `  - ${p.nombre}`, `(-${p.puntos} pts)`, width);
    }
  }
  if (input.puntosGanados != null && input.puntosGanados > 0) {
    pushPointsRow(
      lines,
      "Puntos ganados:",
      `+${input.puntosGanados} pts`,
      width,
    );
  }
  if (input.puntosNuevoSaldo != null) {
    pushPointsRow(
      lines,
      "Saldo total:",
      `${input.puntosNuevoSaldo} pts`,
      width,
    );
  }
  return lines;
}

/**
 * Pie oficial común a los 3 formatos. NO incluye la línea «Imagen no
 * editable» (sin sentido en un ticket) ni duplica WhatsApp/dominio si ya
 * vienen en el mensaje de gracias o en el texto del QR configurado.
 */
export function buildGlobalFooterLines(
  biz: BusinessInfo,
  maxW: number,
  qrLabelText = "",
): string[] {
  const lines: string[] = [];
  const gracias = (biz.mensajeGracias || "¡GRACIAS POR TU COMPRA!").trim();
  pushCenter(lines, gracias, maxW);
  const yaTieneWhatsApp =
    /whatsapp/i.test(gracias) || /whatsapp/i.test(qrLabelText);
  if (biz.whatsapp && !yaTieneWhatsApp) {
    pushCenter(lines, `WhatsApp: ${biz.whatsapp}`, maxW);
  }
  pushCenter(lines, "Gestionado con", maxW);
  pushCenter(lines, "ALTENOT", maxW);
  const site = (biz.sitioWeb || "altenot.com")
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "");
  pushCenter(lines, site, maxW);
  return lines;
}
